import { KeyboardEvent, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import type { ThinkingStep } from '@Models';
import useThinkingDisplay from '../../../CustomHooks/Agents/useThinkingDisplay';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import ThinkingPhaseList from './ThinkingPhaseList';

interface ThinkingProps {
  steps?: ThinkingStep[];
  /** Anchored start timestamp from the runtime so the timer survives unmount. */
  startedAtMs?: number | null;
  /** Agent language, so the placeholder label matches the rest of the turn. */
  lng?: string;
}

const BarPulse = () => (
  <span aria-hidden className="flex h-3.5 items-end gap-[2px]">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-[3px] rounded-full"
        style={{
          backgroundColor: '#7c6cf6',
          animation: `bopsBarPulse 900ms ease-in-out ${i * 120}ms infinite`,
        }}
      />
    ))}
    <style>{`
      @keyframes bopsBarPulse {
        0%, 100% { height: 4px; opacity: 0.55; }
        50% { height: 12px; opacity: 1; }
      }
    `}</style>
  </span>
);

/** Live thinking panel shown until the first answer token arrives. */
const Thinking = ({ steps = [], startedAtMs = null, lng }: ThinkingProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { elapsedSeconds, label } = useThinkingDisplay({ steps, startedAtMs, lng });

  // Only allow expand when there are actual phase steps to show — the current
  // label is already visible inline, so chevroning it open reveals nothing.
  const hasLog = steps.length > 0;

  const toggleExpand = () => {
    if (hasLog) setExpanded((v) => !v);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
      className="flex flex-col overflow-hidden rounded-xl border border-[#10182812] bg-[#fbfaf9]">
      <div
        {...(hasLog && {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': expanded,
          'aria-label': expanded ? t('Agent.AriaHideThinkingLog') : t('Agent.AriaShowThinkingLog'),
          onClick: toggleExpand,
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpand();
            }
          },
        })}
        className={`flex items-center gap-3 px-3 py-2 ${hasLog ? 'cursor-pointer' : ''}`}>
        <BarPulse />

        <div className="relative min-w-0 flex-1 overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              // Keyed by position so a repeated label still animates as a new step.
              key={`${steps.length}-${label}`}
              initial={{ opacity: 0, y: 4, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -4, filter: 'blur(2px)' }}
              transition={{ duration: AGENT_DURATIONS.base, ease: AGENT_EASING.outExpo }}
              className="block">
              <span className="block truncate text-sm font-medium text-gray-800">{label}</span>
            </motion.span>
          </AnimatePresence>
        </div>

        <span className="text-xs tabular-nums text-gray-400" aria-live="polite">
          {elapsedSeconds}s
        </span>

        {hasLog && (
          <span aria-hidden className="flex h-6 w-6 items-center justify-center text-gray-400">
            {expanded ? <ChevronUp size={14} strokeWidth={1.75} /> : <ChevronDown size={14} strokeWidth={1.75} />}
          </span>
        )}
      </div>

      <AnimatePresence initial={false}>
        {expanded && hasLog && (
          <motion.div
            key="log"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: AGENT_DURATIONS.base, ease: AGENT_EASING.outExpo }}
            className="overflow-hidden">
            <ThinkingPhaseList phases={steps} isLive />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Thinking;
